/**
 * `tenants` endpoint wrappers — leases and tenant management.
 *
 * Field shapes are derived from `serializeLease` / `serializeLeaseRow` /
 * `serializeEnhancedTenant` / `serializeCreatedTenant`
 * (refactor/backend/src/modules/tenants/serializers.ts) — see
 * src/types/lease.ts and src/types/tenant.ts for the full derivation notes.
 * Route paths verified against refactor/backend/src/modules/tenants/routes.ts.
 */
import { apiClient, uploadFile } from '../client';
import { LEASES, TENANTS } from './paths';
import type { Paginated } from '../types';
import type { Lease, LeaseCreateRequest, LeaseListRow, LeasePatchRequest } from '../../types/lease';
import type {
  CreatedTenant,
  TenantCreateRequest,
  TenantPatchRequest,
  TenantRow,
} from '../../types/tenant';

// ---------------------------------------------------------------------------
// tenants/leases/
// ---------------------------------------------------------------------------

/**
 * `GET tenants/leases/` — scoped to the caller; `search` matches tenant name / unit number.
 *
 * `tenant` is a Node-only addition with no Django counterpart (see the comment on
 * the `GET /leases/` handler in the backend's tenants/routes.ts); it narrows the
 * already-scoped list to one tenant's leases.
 */
export async function listLeases(params?: {
  page?: number | undefined;
  search?: string | undefined;
  ordering?: string | undefined;
  tenant?: number | undefined;
}): Promise<Paginated<LeaseListRow>> {
  const response = await apiClient.get<Paginated<LeaseListRow>>(LEASES, { params });
  return response.data;
}

/** `GET tenants/leases/{id}/`. */
export async function getLease(id: number): Promise<Lease> {
  const response = await apiClient.get<Lease>(`${LEASES}${id}/`);
  return response.data;
}

/**
 * `POST tenants/leases/`. Creating a lease with `status: 'ACTIVE'` marks the
 * unit occupied and generates the first prorated-rent invoice server-side.
 */
export async function createLease(payload: LeaseCreateRequest): Promise<Lease> {
  const response = await apiClient.post<Lease>(LEASES, payload);
  return response.data;
}

/** `PATCH tenants/leases/{id}/` — partial update. */
export async function updateLease(id: number, payload: LeasePatchRequest): Promise<Lease> {
  const response = await apiClient.patch<Lease>(`${LEASES}${id}/`, payload);
  return response.data;
}

/**
 * `POST tenants/leases/{id}/upload_document/` — NEW, no Django route (see
 * PARITY_NOTES.md). Manager-gated on the backend; requires an existing lease
 * id, so this only applies once a lease has already been created.
 */
export async function uploadLeaseDocument(leaseId: number, file: File): Promise<Lease> {
  const formData = new FormData();
  formData.append('lease_document', file);
  return uploadFile<Lease>(`${LEASES}${leaseId}/upload_document/`, formData);
}

/** `POST tenants/leases/{id}/activate/`. */
export async function activateLease(id: number): Promise<Lease> {
  const response = await apiClient.post<Lease>(`${LEASES}${id}/activate/`);
  return response.data;
}

/** `POST tenants/leases/{id}/terminate/`. */
export async function terminateLease(id: number): Promise<Lease> {
  const response = await apiClient.post<Lease>(`${LEASES}${id}/terminate/`);
  return response.data;
}

/**
 * `DELETE tenants/leases/{id}/` — manager-gated. A lease still referenced by
 * an invoice fails with a 409 rather than deleting.
 */
export async function deleteLease(id: number): Promise<void> {
  await apiClient.delete(`${LEASES}${id}/`);
}

// ---------------------------------------------------------------------------
// tenants/tenants/
// ---------------------------------------------------------------------------

/**
 * `GET tenants/tenants/` — AGENT/SUPERAGENT get `EnhancedTenant` rows, LANDLORD
 * gets the plain `User` shape (see `TenantRow` in src/types/tenant.ts). `search`
 * matches name / email / phone.
 */
export async function listTenants(params?: {
  page?: number | undefined;
  search?: string | undefined;
}): Promise<Paginated<TenantRow>> {
  const response = await apiClient.get<Paginated<TenantRow>>(TENANTS, { params });
  return response.data;
}

/** `GET tenants/tenants/{id}/` — same role-dependent shape as the list. */
export async function getTenant(id: number): Promise<TenantRow> {
  const response = await apiClient.get<TenantRow>(`${TENANTS}${id}/`);
  return response.data;
}

/**
 * `POST tenants/tenants/`. If `password` is omitted the backend defaults it to
 * the literal `'password123'` — see the warning on `TenantCreateRequest`.
 */
export async function createTenant(payload: TenantCreateRequest): Promise<CreatedTenant> {
  const response = await apiClient.post<CreatedTenant>(TENANTS, payload);
  return response.data;
}

/** `PATCH tenants/tenants/{id}/` — writable fields differ by caller role server-side. */
export async function updateTenant(id: number, payload: TenantPatchRequest): Promise<TenantRow> {
  const response = await apiClient.patch<TenantRow>(`${TENANTS}${id}/`, payload);
  return response.data;
}

/**
 * `DELETE tenants/tenants/{id}/` — manager-gated. The backend cascades the
 * tenant's profile, notifications and leases; a tenant still referenced by an
 * invoice fails with a 409 rather than deleting.
 */
export async function deleteTenant(id: number): Promise<void> {
  await apiClient.delete(`${TENANTS}${id}/`);
}
